import type { CivilDate, TimeSlot } from '@hue/design-system';
import type {
  Appointment,
  CalendarHoliday,
  ScheduleBlock,
  SchedulingResource,
} from '../../lib/api.js';
import type { OperationalMonth } from '../../app/operational-period-context.js';

/// Regras do calendário que não dependem de tela: datas civis, faixas de sobreposição e se um
/// horário cabe. Ficam aqui, puras, para a grade, o arraste e o formulário concordarem entre si —
/// e para poderem ser testadas sem montar a grade.

export type CalendarView = 'month' | 'week' | 'day';

export const SLOT_MINUTES = 15;
export const DAY_MINUTES = 24 * 60;
/// Faixa mínima da grade de horários. Cresce quando algum expediente ou atendimento sai dela.
const GRID_START = 7 * 60;
const GRID_END = 21 * 60;

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAY_LONG = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

function utc(date: CivilDate): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

function civil(value: Date): CivilDate {
  return value.toISOString().slice(0, 10);
}

export function addDays(date: CivilDate, days: number): CivilDate {
  const next = utc(date);
  next.setUTCDate(next.getUTCDate() + days);
  return civil(next);
}

export function weekdayOf(date: CivilDate): number {
  return utc(date).getUTCDay();
}

export function dayOfMonth(date: CivilDate): number {
  return utc(date).getUTCDate();
}

export function monthOf(date: CivilDate): `${number}-${string}` {
  return date.slice(0, 7) as `${number}-${string}`;
}

/// A semana começa na segunda, como no resto da agenda.
export function startOfWeek(date: CivilDate): CivilDate {
  return addDays(date, -((weekdayOf(date) + 6) % 7));
}

/// Os dias da grade mensal: da segunda antes do dia 1º ao domingo depois do último dia.
export function monthGrid(date: CivilDate): CivilDate[] {
  const first = `${monthOf(date)}-01`;
  const start = startOfWeek(first);
  const next = utc(first);
  next.setUTCMonth(next.getUTCMonth() + 1);
  const last = addDays(civil(next), -1);
  const days: CivilDate[] = [];
  for (let day = start; day <= last || days.length % 7 !== 0; day = addDays(day, 1)) days.push(day);
  return days;
}

/// No celular a semana mostra três dias a partir do dia à vista; sete não cabem com leitura.
export function visibleDays(view: CalendarView, date: CivilDate, narrow: boolean): CivilDate[] {
  if (view === 'month') return monthGrid(date);
  if (view === 'day') return [date];
  const start = narrow ? date : startOfWeek(date);
  return Array.from({ length: narrow ? 3 : 7 }, (_item, index) => addDays(start, index));
}

export function periodRange(
  view: CalendarView,
  date: CivilDate,
  narrow: boolean,
): { from: CivilDate; to: CivilDate } {
  const days = visibleDays(view, date, narrow);
  return { from: days[0]!, to: days[days.length - 1]! };
}

export function shiftDate(
  view: CalendarView,
  date: CivilDate,
  direction: 1 | -1,
  narrow: boolean,
): CivilDate {
  if (view === 'day') return addDays(date, direction);
  if (view === 'week') return addDays(date, direction * (narrow ? 3 : 7));
  const moved = utc(`${monthOf(date)}-01`);
  moved.setUTCMonth(moved.getUTCMonth() + direction);
  return civil(moved);
}

export function monthName(date: CivilDate): string {
  return MONTHS[utc(date).getUTCMonth()]!;
}

export function periodLabel(view: CalendarView, date: CivilDate, narrow: boolean): string {
  const year = date.slice(0, 4);
  if (view === 'month') {
    const name = monthName(date);
    return `${name[0]!.toUpperCase()}${name.slice(1)} de ${year}`;
  }
  if (view === 'day') {
    return `${WEEKDAY_LONG[weekdayOf(date)]}, ${dayOfMonth(date)} de ${monthName(date)} de ${year}`;
  }
  const { from, to } = periodRange(view, date, narrow);
  const short = (day: CivilDate) => monthName(day).slice(0, 3);
  return monthOf(from) === monthOf(to)
    ? `${dayOfMonth(from)} – ${dayOfMonth(to)} de ${short(to)} de ${to.slice(0, 4)}`
    : `${dayOfMonth(from)} ${short(from)} – ${dayOfMonth(to)} ${short(to)} de ${to.slice(0, 4)}`;
}

export function dayLabel(date: CivilDate): string {
  return `${WEEKDAY_LONG[weekdayOf(date)]}, ${dayOfMonth(date)} de ${monthName(date)}`;
}

export function minuteLabel(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function minutesOf(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function snap(minute: number, step = SLOT_MINUTES): number {
  return Math.round(minute / step) * step;
}

/// Um instante lido no fuso da empresa: o dia civil e o minuto desse dia.
export function civilMoment(value: string, timeZone: string): { date: CivilDate; minute: number } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(new Date(value))
      .map((part) => [part.type, part.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minute: (Number(parts.hour) % 24) * 60 + Number(parts.minute),
  };
}

/// Um intervalo da agenda já no dia civil. Terminar à meia-noite é terminar no minuto 1440 do mesmo
/// dia, e não no minuto zero do seguinte.
export interface PlacedInterval {
  date: CivilDate;
  start: number;
  end: number;
}

export function placeInterval(
  interval: { startsAt: string; endsAt: string },
  timeZone: string,
): PlacedInterval {
  const start = civilMoment(interval.startsAt, timeZone);
  const end = civilMoment(interval.endsAt, timeZone);
  return {
    date: start.date,
    start: start.minute,
    end: end.date === start.date ? end.minute : DAY_MINUTES,
  };
}

export type PlacedAppointment = Appointment & PlacedInterval;
export type PlacedBlock = ScheduleBlock & PlacedInterval;

export function placeAppointments(items: Appointment[], timeZone: string): PlacedAppointment[] {
  return items.map((item) => ({ ...item, ...placeInterval(item, timeZone) }));
}

export function placeBlocks(items: ScheduleBlock[], timeZone: string): PlacedBlock[] {
  return items.map((item) => ({ ...item, ...placeInterval(item, timeZone) }));
}

/// Faixas lado a lado para o que se sobrepõe no mesmo dia: cada grupo encadeado de sobreposições
/// divide a largura pelo maior número de itens simultâneos.
export function layoutLanes<T extends { id: string; start: number; end: number }>(
  items: T[],
): Map<string, { lane: number; lanes: number }> {
  const sorted = [...items].sort((left, right) => left.start - right.start || right.end - left.end);
  const layout = new Map<string, { lane: number; lanes: number }>();
  let group: Array<{ id: string; lane: number }> = [];
  let columns: number[] = [];
  let groupEnd = -1;
  const flush = () => {
    for (const item of group) layout.set(item.id, { lane: item.lane, lanes: columns.length });
    group = [];
    columns = [];
  };
  for (const item of sorted) {
    if (group.length > 0 && item.start >= groupEnd) flush();
    let lane = columns.findIndex((end) => end <= item.start);
    if (lane < 0) {
      lane = columns.length;
      columns.push(0);
    }
    columns[lane] = item.end;
    group.push({ id: item.id, lane });
    groupEnd = group.length === 1 ? item.end : Math.max(groupEnd, item.end);
  }
  flush();
  return layout;
}

/// Junta os bloqueios que se encostam ou se sobrepõem num trecho só: a grade mostra uma barra por
/// trecho, e a dica dela lista todos os bloqueios que o compõem.
export function groupBlocks<T extends { start: number; end: number }>(
  items: T[],
): Array<{ start: number; end: number; blocks: T[] }> {
  const sorted = [...items].sort((left, right) => left.start - right.start || left.end - right.end);
  const groups: Array<{ start: number; end: number; blocks: T[] }> = [];
  for (const item of sorted) {
    const last = groups.at(-1);
    if (last && item.start <= last.end) {
      last.end = Math.max(last.end, item.end);
      last.blocks.push(item);
    } else {
      groups.push({ start: item.start, end: item.end, blocks: [item] });
    }
  }
  return groups;
}

/// Janelas de atendimento do recurso naquele dia da semana.
/// O feriado que muda o dia na unidade (ADR 0087); a API só manda os que mudam alguma coisa.
export function holidayOn(
  holidays: readonly CalendarHoliday[] | undefined,
  unitId: string | undefined,
  date: CivilDate,
): CalendarHoliday | undefined {
  return holidays?.find((holiday) => holiday.date === date && holiday.unitId === unitId);
}

/// O expediente do recurso no dia. No feriado, fechado não tem faixa e equipe reduzida tem a faixa
/// do dia, só para quem está marcado.
export function windowsOn(
  resource:
    | (Pick<SchedulingResource, 'availability'> &
        Partial<Pick<SchedulingResource, 'id' | 'businessUnitId'>>)
    | undefined,
  date: CivilDate,
  holidays?: readonly CalendarHoliday[],
): Array<{ start: number; end: number }> {
  if (!resource) return [];
  const holiday = holidayOn(holidays, resource.businessUnitId, date);
  if (holiday?.mode === 'CLOSED') return [];
  if (holiday?.mode === 'REDUCED')
    return resource.id && holiday.resourceIds.includes(resource.id)
      ? [{ start: holiday.startMinute!, end: holiday.endMinute! }]
      : [];
  const weekday = weekdayOf(date);
  return resource.availability
    .filter((window) => window.weekday === weekday)
    .map((window) => ({ start: window.startMinute, end: window.endMinute }));
}

/// A faixa de horas da grade: pelo menos 07h–21h, alargada para caber expediente e atendimento.
export function gridRange(
  resources: SchedulingResource[],
  items: Array<{ start: number; end: number }>,
): { start: number; end: number } {
  const windows = resources.flatMap((resource) =>
    resource.availability.map((window) => ({
      start: window.startMinute,
      end: window.endMinute,
    })),
  );
  const all = [...windows, ...items];
  const start = Math.min(GRID_START, ...all.map((item) => item.start));
  const end = Math.max(GRID_END, ...all.map((item) => item.end));
  return {
    start: Math.floor(start / 60) * 60,
    end: Math.min(DAY_MINUTES, Math.ceil(end / 60) * 60),
  };
}

export interface SlotCandidate {
  appointmentId?: string | undefined;
  professionalId: string;
  roomId: string | null;
  date: CivilDate;
  start: number;
  end: number;
}

/// O que a prévia sabe dizer de um horário. `problem` impede — bloqueio ou alguém já no horário —,
/// na ordem em que a API recusaria. `warning` só avisa: fora do expediente é exceção legítima
/// (ADR 0034, decisão 9). Os dois vazios quando o horário serve. É só a
/// prévia: a API continua sendo quem decide, com o que outra pessoa pode ter gravado agora.
export interface SlotCheck {
  problem: string;
  warning: string;
}

export function slotCheck(
  context: {
    allowAppointmentOverlap?: boolean;
    resources: SchedulingResource[];
    appointments: PlacedAppointment[];
    blocks: PlacedBlock[];
    holidays?: readonly CalendarHoliday[] | undefined;
  },
  candidate: SlotCandidate,
): SlotCheck {
  if (candidate.roomId === 'ANY') {
    const checks = context.resources
      .filter((resource) => resource.kind === 'ROOM' && resource.active)
      .map((resource) => slotCheck(context, { ...candidate, roomId: resource.id }));
    return (
      checks.find((check) => !check.problem && !check.warning) ??
      checks.find(
        (check) => !check.problem && !check.warning.startsWith('Sobreposição de horário:'),
      ) ??
      checks.find((check) => !check.problem) ?? {
        problem: checks[0]?.problem || 'nenhuma sala disponível',
        warning: '',
      }
    );
  }
  if (candidate.professionalId === 'ANY') {
    const checks = context.resources
      .filter(
        (resource) =>
          resource.kind === 'PROFESSIONAL' && resource.active && resource.attendsSchedule !== false,
      )
      .map((resource) => slotCheck(context, { ...candidate, professionalId: resource.id }));
    return (
      checks.find((check) => !check.problem && !check.warning) ??
      checks.find(
        (check) => !check.problem && !check.warning.startsWith('Sobreposição de horário:'),
      ) ??
      checks.find((check) => !check.problem) ?? {
        problem: checks[0]?.problem || 'nenhum profissional disponível',
        warning: '',
      }
    );
  }
  const problem = slotProblem(context, candidate);
  const warning = slotWarning(context, candidate);
  if (context.allowAppointmentOverlap && (problem === 'ocupado' || problem === 'sala ocupada'))
    return {
      problem: '',
      warning: [
        `Sobreposição de horário: ${problem === 'ocupado' ? 'profissional ocupado' : 'sala ocupada'}`,
        warning,
      ]
        .filter(Boolean)
        .join(' · '),
    };
  return { problem, warning };
}

function slotWarning(
  context: { resources: SchedulingResource[]; holidays?: readonly CalendarHoliday[] | undefined },
  candidate: SlotCandidate,
): string {
  const professional = context.resources.find((item) => item.id === candidate.professionalId);
  const fits = (resource: SchedulingResource | undefined) =>
    windowsOn(resource, candidate.date, context.holidays).some(
      (window) => window.start <= candidate.start && window.end >= candidate.end,
    );
  const room = candidate.roomId
    ? context.resources.find((item) => item.id === candidate.roomId)
    : undefined;
  const holiday = holidayOn(context.holidays, professional?.businessUnitId, candidate.date);
  if (holiday?.mode === 'CLOSED') return 'feriado';
  if (holiday?.mode === 'REDUCED') {
    if (!holiday.resourceIds.includes(candidate.professionalId)) return 'folga no feriado';
    if (!fits(professional)) return 'fora do horário do feriado';
    if (candidate.roomId && !fits(room)) return 'sala fechada no feriado';
    return '';
  }
  if (windowsOn(professional, candidate.date).length === 0) return 'não atende neste dia';
  if (!fits(professional)) return 'fora do expediente';
  if (candidate.roomId && !fits(room)) return 'sala fora do expediente';
  return '';
}

function slotProblem(
  context: {
    resources: SchedulingResource[];
    appointments: PlacedAppointment[];
    blocks: PlacedBlock[];
  },
  candidate: SlotCandidate,
): string {
  const overlaps = (item: PlacedInterval) =>
    item.date === candidate.date && candidate.start < item.end && candidate.end > item.start;
  const blocked = (resourceId: string | null) =>
    context.blocks.some((block) => overlaps(block) && block.resourceId === resourceId);
  // Diz de quem é o bloqueio: o profissional livre numa sala bloqueada não é "bloqueado".
  if (blocked(candidate.professionalId)) return 'bloqueado';
  if (candidate.roomId && blocked(candidate.roomId)) return 'sala bloqueada';
  const live = context.appointments.filter(
    (item) =>
      item.id !== candidate.appointmentId &&
      item.status !== 'CANCELLED' &&
      item.status !== 'NO_SHOW' &&
      !(item.requiresConfirmation && item.status === 'SCHEDULED') &&
      overlaps(item),
  );
  if (live.some((item) => item.professionalId === candidate.professionalId)) return 'ocupado';
  // A sala comporta a capacidade dela: um salão com cinco cadeiras recebe cinco juntos (ADR 0035).
  const room = candidate.roomId
    ? context.resources.find((item) => item.id === candidate.roomId)
    : undefined;
  const inRoom = live.filter((item) => item.roomId === candidate.roomId);
  if (candidate.roomId && peakOverlap(inRoom, candidate) >= (room?.capacity ?? 1)) {
    return 'sala ocupada';
  }
  return '';
}

/// Quantos intervalos se sobrepõem no instante mais cheio da janela. Borda encostada não soma: quem
/// sai às 11:00 libera a cadeira de quem entra às 11:00.
export function peakOverlap(
  items: ReadonlyArray<{ start: number; end: number }>,
  window: { start: number; end: number },
): number {
  const edges = items
    .map((item) => ({
      start: Math.max(item.start, window.start),
      end: Math.min(item.end, window.end),
    }))
    .filter((item) => item.start < item.end)
    .flatMap((item) => [
      { at: item.start, delta: 1 },
      { at: item.end, delta: -1 },
    ])
    .sort((left, right) => left.at - right.at || left.delta - right.delta);
  let current = 0;
  let peak = 0;
  for (const edge of edges) {
    current += edge.delta;
    peak = Math.max(peak, current);
  }
  return peak;
}

/// Os horários que o seletor oferece: o dia inteiro, em passos de 15 minutos, cada um com o motivo
/// quando está bloqueado ou ocupado. O que cai fora do expediente do profissional ou da sala continua
/// escolhível e vem marcado, para o painel pintar diferente (ADR 0034, decisão 9). Sem profissional
/// escolhido, não há o que dizer.
export function availableSlots(
  context: Parameters<typeof slotCheck>[0],
  form: {
    appointmentId?: string | undefined;
    professionalId: string;
    roomId: string | null;
    date: CivilDate;
    durationMinutes: number;
  },
): TimeSlot[] | undefined {
  if (
    form.professionalId !== 'ANY' &&
    !context.resources.some((item) => item.id === form.professionalId)
  )
    return undefined;
  const slots: TimeSlot[] = [];
  for (let start = 0; start < DAY_MINUTES; start += SLOT_MINUTES) {
    const candidate = {
      appointmentId: form.appointmentId,
      professionalId: form.professionalId,
      roomId: form.roomId,
      date: form.date,
      start,
      end: start + form.durationMinutes,
    };
    const { problem } = slotCheck(context, candidate);
    const warning = slotWarning(context, candidate);
    // Atendimento que atravessa a meia-noite a API recusa; o painel mostra antes.
    const reason = start + form.durationMinutes > DAY_MINUTES ? 'passa da meia-noite' : problem;
    slots.push({
      time: minuteLabel(start),
      ...(reason ? { unavailableReason: reason } : {}),
      ...(warning ? { offHours: true } : {}),
    });
  }
  return slots;
}

/// Cor de série de cada profissional, pela ordem da lista: a mesma pessoa tem sempre a mesma cor
/// enquanto a lista não mudar. Oito cores e depois repete.
export function seriesColor(index: number): string {
  return `var(--series-${(index % 8) + 1})`;
}

/// Aplica a escolha de um filtro. Tudo marcado volta a ser "todos" (`undefined`), que é o que sai
/// da URL; desmarcar o último deixa a lista vazia, que é "nenhum".
export function toggleFilter(
  current: string[] | undefined,
  all: string[],
  value: string,
): string[] | undefined {
  const selected = new Set(current ?? all);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  const next = all.filter((item) => selected.has(item));
  return next.length === all.length ? undefined : next;
}

/// O dia que o calendário mostra. Segue o mês de trabalho da casca (ADR 0020): se o dia da URL não
/// é desse mês, vale hoje, quando o mês é o atual, ou o dia 1º.
export function calendarDate(
  urlDate: CivilDate | null,
  month: OperationalMonth,
  today: CivilDate,
): CivilDate {
  if (month === 'ALL') return urlDate ?? today;
  if (urlDate && monthOf(urlDate) === month) return urlDate;
  return monthOf(today) === month ? today : `${month}-01`;
}

/// O que sobra da faixa da grade fora das janelas de atendimento.
export function complement(
  windows: Array<{ start: number; end: number }>,
  range: { start: number; end: number },
): Array<{ start: number; end: number }> {
  const sorted = [...windows].sort((left, right) => left.start - right.start);
  const out: Array<{ start: number; end: number }> = [];
  let cursor = range.start;
  for (const window of sorted) {
    if (window.start > cursor) out.push({ start: cursor, end: window.start });
    cursor = Math.max(cursor, window.end);
  }
  if (cursor < range.end) out.push({ start: cursor, end: range.end });
  return out;
}

export function professionalLabel(
  appointment: Pick<Appointment, 'anyProfessional' | 'status' | 'professionalDisplayName'>,
): string {
  return appointment.anyProfessional && appointment.status === 'SCHEDULED'
    ? `Qualquer disponível · ${appointment.professionalDisplayName} (alocação provisória)`
    : appointment.professionalDisplayName;
}

export function roomLabel(
  appointment: Pick<Appointment, 'anyRoom' | 'status' | 'roomDisplayName'>,
): string {
  return appointment.anyRoom && appointment.status === 'SCHEDULED'
    ? `${appointment.roomDisplayName ?? 'Sala disponível'} · alocação provisória`
    : (appointment.roomDisplayName ?? 'Sem sala');
}
